/**
 * Pure mappers from PocketBase records to the camelCase party domain types.
 *
 * Kept separate from the store so they're trivially testable and the
 * record-vs-domain seam is in one place. The reverse direction (writes) goes
 * through hook routes and typed collection writes, so only read-mapping lives
 * here.
 *
 * Two shape differences from the Supabase rows these replace. Relations are
 * named after what they point at, so `party_id` is `party` and `user_id` is
 * `user`; and PocketBase has no NULL for text or numbers, so an absent value
 * arrives as `''` or `0` and is mapped back to `undefined` here rather than
 * leaking a falsy sentinel into the domain.
 */
import {
  type PartyInviteRecord,
  type PartyMemberRecord,
  type PartyRecord,
  type PartySubscriptionRecord,
} from '@/shared/api'
import { type PackPointer } from '@/entities/content'
import type { Party, PartyInvite, PartyMember, PartyPackSubscription } from '../types/party'

export function toParty(r: PartyRecord): Party {
  return {
    id: r.id,
    name: r.name,
    ownerId: r.owner,
    membersCanSeeCharacters: r.members_can_see_characters,
    createdAt: r.created,
  }
}

/**
 * `username` comes from `expand: 'user'`, which is why it is read off the record
 * rather than passed in. Supabase needed a second query for this: both
 * `party_members` and `profiles` pointed at `auth.users`, so neither could be
 * joined to the other. The username now lives on the user record itself.
 */
export function toPartyMember(r: PartyMemberRecord): PartyMember {
  return {
    id: r.id,
    partyId: r.party,
    userId: r.user,
    role: r.role,
    characterId: r.character || undefined,
    joinedAt: r.joined,
    username: r.expand?.user?.name,
  }
}

export function toPartyInvite(r: PartyInviteRecord): PartyInvite {
  return {
    id: r.id,
    partyId: r.party,
    invitedBy: r.invited_by,
    kind: r.kind,
    email: r.email || undefined,
    code: r.code || undefined,
    acceptedBy: r.accepted_by || undefined,
    status: r.status,
    // 0 means unlimited, which is what NULL meant in Postgres.
    maxUses: r.max_uses || undefined,
    usedCount: r.used_count,
    expiresAt: r.expires || undefined,
    createdAt: r.created,
    inviterName: r.expand?.invited_by?.name,
  }
}

export function toPartySubscription(r: PartySubscriptionRecord): PartyPackSubscription {
  return {
    id: r.id,
    partyId: r.party,
    // A `json` field; the pointer is written by this app and read back as it was.
    pointer: r.pointer as unknown as PackPointer,
    addedBy: r.added_by || undefined,
    createdAt: r.created,
  }
}
